import type { MessageObject, MessageValue } from "@messageformat/react";
import { useContext } from "react";

import { getMessage, MessageContext } from "@messageformat/react";

const format = (template: string, ...params: string[]): string => {
  let formattedString = template;
  params.forEach((param: string, index: number) => {
    formattedString = formattedString.replace(new RegExp(`\\{${index}\\}`, "g"), param);
  });

  return formattedString;
};

/**
 * This is an array that looks like e.g. ["{0}", "{1}", "{2}", ...]
 * This exists as a temporary workaround to allow us to continue to use format() to do formatting
 * though messageformat is designed to handle the formatting itself. Passing this as arguments to
 * messageformat functions will allow us to convert the function back to a format string
 */
const temporaryFormatPlaceholders = Array.from({ length: 9 }, (_, i) => `{${i}}`);

/**
 * A react hook that gets a given partner resource string from the ResourceStringContext and
 * formats with given params accordingly.
 * @param {TResourceKey} key The key for the resource string
 * @param {string} partnerId The partner Id from where resource strings should be taken
 * @param {string[]} args The optional list of arguments to be formatted in the resource string
 * @returns {string} The resource string from the key formatted as appropriate
 */
export const usePartnerResourceString = <TResourceKey extends string>(
  key: TResourceKey,
  partnerId: string,
  ...args: string[]
): string => {
  const message = getMessage(useContext(MessageContext), [partnerId, key]) as (
    args: string[]
  ) => string;

  try {
    return message(args.length > 0 ? args : temporaryFormatPlaceholders);
  } catch (e) {
    console.log("key", key);
    console.log("message", message);
    return "";
  }
};

/**
 * A react hook that gets a list of partner resource strings from the ResourceStringContext
 * Does not handle formatting. To format corresponding strings, use @gamedev-web/platform-sdk/format
 * @example
 *  const [ string1, string2, string3 ] = useResourceStringsFromPartner(ResourceStrings.string1, ResourceStrings.string2, ResourceString.string3);
 * @param {string} partnerId The partner Id from where resource strings should be taken
 * @param {string[]} keys A list of keys to get resource strings for
 * @returns {string[]} The list of resource strings as per requested
 */
export const usePartnerResourceStrings = <TResourceKey extends string>(
  partnerId: string,
  ...keys: TResourceKey[]
): string[] => {
  const context = useContext(MessageContext);

  return keys.map(key => {
    const messageFunction = getMessage(context, [partnerId, key]) as (args: string[]) => string;
    return messageFunction(temporaryFormatPlaceholders);
  });
};

function searchForMessage(messageObject: MessageObject | MessageValue, path: string[]) {
  let messages = messageObject;
  if (messages) {
    for (const pathSegment of path) {
      if (typeof messages !== "object") {
        return undefined;
      }
      messages = messages[pathSegment];
      if (messages === undefined) {
        return undefined;
      }
    }
  }
  return messages;
}

function normalizeResourcePath(key: string | string[], pathSep: string | null) {
  if (Array.isArray(key)) {
    return key;
  }
  return pathSep ? key.split(pathSep) : [key];
}

/**
 * Like useResourceString, but instead of logging an error when a string is missing
 * it will fall back to the default string. Useful for situations where a key is dynamic
 * and there may or may not be a translation for it.
 */
export function usePartnerResourceStringOrDefault<TResourceKey extends string>(
  partnerId: string,
  key: TResourceKey | string,
  defaultString: string | undefined,
  ...args: string[]
) {
  const context = useContext(MessageContext);
  const { locales, messages, pathSep } = context;
  const path = normalizeResourcePath([partnerId, key], pathSep);
  for (const locale of locales) {
    const msg = searchForMessage(messages[locale], path);
    if (msg !== undefined && typeof msg === "function") {
      return msg(args) as string;
    }
  }
  return defaultString ? format(defaultString, ...args) : undefined;
}

type MessageParams = Record<string, string | number> | string[];
type MessageFunction = (params?: MessageParams) => string;

export function usePartnerMessageFunction<TResourceKey extends string>(
  partnerId: string,
  key: TResourceKey
) {
  return getMessage(useContext(MessageContext), [partnerId, key]) as MessageFunction;
}

export function usePartnerMessage<TResourceKey extends string>(
  partnerId: string,
  key: TResourceKey,
  params?: MessageParams
) {
  const messageFunction = usePartnerMessageFunction(partnerId, key);

  return messageFunction(params);
}

export function usePartnerMessages<TResourceKey extends string>(
  partnerId: string,
  ...keys: TResourceKey[]
) {
  const context = useContext(MessageContext);
  const strings = {} as Record<TResourceKey, string>;
  keys.forEach(key => {
    const message = getMessage(context, [partnerId, key]);
    strings[key] = (typeof message === "function" ? message() : message) as string;
  });
  return strings;
}
